import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  CondenserLocationType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GridSpaceDataType
} from 'data/types';
import { toggleCondenserLocation } from 'features/structure';
import { useMarkStructureStepCompleted } from 'hooks/use-mark-structure-step-completed';
import { useCallback } from 'react';
import { getOrientedExposure } from 'utils/structure';
import { getStructureOverlayBounds } from 'utils/structure-overlay-bounds';
import CondenserLocationLabel from './condenser-location-label';
import {
  LOCATION_BOX_CLASSES,
  getSideLabel
} from './condenser-locations-overlay-helpers';

const SIDE_BOX_SIZE = 80;
const ROOF_AREA_RATIO = 0.8;

type CondenserLocationsOverlayProps = Readonly<{
  gridSpaces: GridSpaceDataType[];
  zoom: number;
  panOffset: { x: number; y: number };
  footprintPolygons?: FootprintPolygonsType | null;
}>;

export default function CondenserLocationsOverlay({
  gridSpaces,
  zoom,
  panOffset,
  footprintPolygons
}: CondenserLocationsOverlayProps) {
  const dispatch = useAppDispatch();
  const { condenserLocations } = useAppSelector((state) => state.structure);
  const { projectSettings } = useAppSelector((state) => state.project);
  const markStructureStepCompleted = useMarkStructureStepCompleted();

  const orientationSetting = projectSettings.find(
    (s) => s.code === 'structure_orientation'
  );
  const orientation = orientationSetting?.value as
    | ExposureDirectionType
    | undefined;

  const handleLocationClick = useCallback(
    (location: CondenserLocationType, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(toggleCondenserLocation(location));
      markStructureStepCompleted('base_layout', 'condenser_locations');
    },
    [dispatch, markStructureStepCompleted]
  );

  const bounds = getStructureOverlayBounds(gridSpaces, footprintPolygons);
  if (!bounds) return null;

  const { minX, minY, maxX, maxY } = bounds;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const roofWidth = (maxX - minX) * ROOF_AREA_RATIO;
  const roofHeight = (maxY - minY) * ROOF_AREA_RATIO;
  const isSelected = (location: CondenserLocationType) =>
    condenserLocations?.includes(location) ?? false;

  return (
    <div className='pointer-events-none absolute inset-0 z-10' aria-hidden>
      <div
        className='pointer-events-auto absolute inset-0'
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
          transformOrigin: '0 0'
        }}
      >
        <button
          type='button'
          id='condenser-location-top'
          className={LOCATION_BOX_CLASSES}
          style={{
            left: minX,
            top: minY - SIDE_BOX_SIZE,
            width: maxX - minX,
            height: SIDE_BOX_SIZE
          }}
          onClick={(e) => handleLocationClick('top', e)}
        >
          <CondenserLocationLabel
            label={getSideLabel(getOrientedExposure('top', orientation))}
            isSelected={isSelected('top')}
          />
        </button>
        <button
          type='button'
          id='condenser-location-right'
          className={LOCATION_BOX_CLASSES}
          style={{
            left: maxX,
            top: minY,
            width: SIDE_BOX_SIZE,
            height: maxY - minY
          }}
          onClick={(e) => handleLocationClick('right', e)}
        >
          <CondenserLocationLabel
            label={getSideLabel(getOrientedExposure('right', orientation))}
            isSelected={isSelected('right')}
          />
        </button>
        <button
          type='button'
          id='condenser-location-bottom'
          className={LOCATION_BOX_CLASSES}
          style={{
            left: minX,
            top: maxY,
            width: maxX - minX,
            height: SIDE_BOX_SIZE
          }}
          onClick={(e) => handleLocationClick('bottom', e)}
        >
          <CondenserLocationLabel
            label={getSideLabel(getOrientedExposure('bottom', orientation))}
            isSelected={isSelected('bottom')}
          />
        </button>
        <button
          type='button'
          id='condenser-location-left'
          className={LOCATION_BOX_CLASSES}
          style={{
            left: minX - SIDE_BOX_SIZE,
            top: minY,
            width: SIDE_BOX_SIZE,
            height: maxY - minY
          }}
          onClick={(e) => handleLocationClick('left', e)}
        >
          <CondenserLocationLabel
            label={getSideLabel(getOrientedExposure('left', orientation))}
            isSelected={isSelected('left')}
          />
        </button>
        <button
          type='button'
          id='condenser-location-roof'
          className={LOCATION_BOX_CLASSES}
          style={{
            left: centerX - roofWidth / 2,
            top: centerY - roofHeight / 2,
            width: roofWidth,
            height: roofHeight
          }}
          onClick={(e) => handleLocationClick('roof', e)}
        >
          <CondenserLocationLabel
            label='Roof'
            isSelected={isSelected('roof')}
          />
        </button>
      </div>
    </div>
  );
}
